/**
 * Hull shape for boat-to-boat contact. A boat is a capsule: a spine along the
 * keel with a rounded cap at each end, so bow and transom are the same curve the
 * beam is. An ellipse is the truer outline, but two ellipses have no closed-form
 * contact normal; two capsules resolve in a handful of dot products, which is
 * what the sweep needs per pair per step.
 *
 * Collision geometry only. Nothing here knows about the map, damage, or Phaser.
 */
import { contactShape, type HullDimensions } from '../../hullSize.ts';
import { add, clamp, dot, fromAngle, length, lengthSq, scale, sub } from '../../math.ts';
import type { Vec2 } from '../../types.ts';

const EPSILON = 1e-9;

export type { HullDimensions };

export type HullCapsule = {
  center: Vec2;
  heading: number;
  /** Half the spine, so `(length - beam) / 2` in world pixels. Zero makes the hull a disc. */
  halfSpine: number;
  /** Half the beam, in world pixels. */
  radius: number;
};

export type HullContact = {
  /** Midway between the two touching surface points. */
  point: Vec2;
  /** Unit, pointing from the first hull toward the second. */
  normal: Vec2;
  /** World pixels of overlap. Zero is a graze; larger is deeper. */
  depth: number;
};

export function hullCapsule(
  dimensions: HullDimensions,
  center: Vec2,
  heading: number,
): HullCapsule {
  // `HULL_CONTACT_SCALE` of the drawn hull, so a brush that looks clear is clear. A beam at or
  // over the length would invert the spine; that hull is just a disc.
  const shape = contactShape(dimensions);

  return { center: { ...center }, heading, halfSpine: shape.halfSpine, radius: shape.radius };
}

/** Bow and transom ends of the spine, in world space. */
export function capsuleSpine(capsule: HullCapsule): { from: Vec2; to: Vec2 } {
  const half = fromAngle(capsule.heading, capsule.halfSpine);

  return { from: sub(capsule.center, half), to: add(capsule.center, half) };
}

/** Everything the hull can reach from its centre. The sweep rejects pairs on this first. */
export function hullReach(capsule: HullCapsule): number {
  return capsule.halfSpine + capsule.radius;
}

/**
 * Overlap between two hulls, or null when they are clear of each other.
 * The normal points from `a` to `b`, so `a` is pushed back along it.
 */
export function hullContact(a: HullCapsule, b: HullCapsule): HullContact | null {
  const reach = hullReach(a) + hullReach(b);
  const span = sub(b.center, a.center);

  if (lengthSq(span) > reach * reach) {
    return null;
  }

  const spineA = capsuleSpine(a);
  const spineB = capsuleSpine(b);
  const closest = closestPointsOnSegments(spineA.from, spineA.to, spineB.from, spineB.to);
  const between = sub(closest.onB, closest.onA);
  const gap = length(between);
  const touching = a.radius + b.radius;

  if (gap > touching) {
    return null;
  }

  const normal =
    gap > EPSILON
      ? scale(between, 1 / gap)
      : // Spines cross, so there is no direction between them. Fall back to the line of
        // centres, then to a's beam, rather than returning a zero normal the solver cannot use.
        fallbackNormal(a, span);
  const surfaceA = add(closest.onA, scale(normal, a.radius));
  const surfaceB = sub(closest.onB, scale(normal, b.radius));

  return {
    point: scale(add(surfaceA, surfaceB), 0.5),
    normal,
    depth: touching - gap,
  };
}

function fallbackNormal(a: HullCapsule, span: Vec2): Vec2 {
  const magnitude = length(span);

  if (magnitude > EPSILON) {
    return scale(span, 1 / magnitude);
  }

  return fromAngle(a.heading + Math.PI / 2, 1);
}

/**
 * Closest pair of points on two segments (Ericson, Real-Time Collision Detection).
 * Handles a degenerate segment at either end, which is the disc-shaped hull.
 */
function closestPointsOnSegments(
  p1: Vec2,
  q1: Vec2,
  p2: Vec2,
  q2: Vec2,
): { onA: Vec2; onB: Vec2 } {
  const d1 = sub(q1, p1);
  const d2 = sub(q2, p2);
  const r = sub(p1, p2);
  const a = lengthSq(d1);
  const e = lengthSq(d2);
  const f = dot(d2, r);
  let s = 0;
  let t = 0;

  if (a <= EPSILON && e <= EPSILON) {
    return { onA: { ...p1 }, onB: { ...p2 } };
  }

  if (a <= EPSILON) {
    t = clamp(f / e, 0, 1);
  } else {
    const c = dot(d1, r);

    if (e <= EPSILON) {
      s = clamp(-c / a, 0, 1);
    } else {
      const b = dot(d1, d2);
      const denom = a * e - b * b;

      s = denom > EPSILON ? clamp((b * f - c * e) / denom, 0, 1) : 0;
      t = (b * s + f) / e;

      if (t < 0) {
        t = 0;
        s = clamp(-c / a, 0, 1);
      } else if (t > 1) {
        t = 1;
        s = clamp((b - c) / a, 0, 1);
      }
    }
  }

  return { onA: add(p1, scale(d1, s)), onB: add(p2, scale(d2, t)) };
}
